import { WEB_API_BASE_PATH } from "common/constants";
import { fetchWithTimeout, handleFetchError } from "common/utils/fetchHelpers";
import { setBakedMaterialsFileUrlHelper } from "gfx/bakedMaterialsFileUrlHelperRegistry";
import { getWithTimeout, postFormDataWithTimeout, getFetchHeaders } from "lib/utils/fetchAuthHelpers";
import type { StorageResponse } from "./types";

/**
 * postFile uploads a file to the current environments file store. It is the only known client of this endpoint
 * and requires authentication.
 *
 * @param file the content of the file
 * @param name a name for the file
 * @param preserveFileName whether to prepend a UID to the file to avoid name-clashing. You usually want this so it is the default.
 * @param usePrivateBucket using a private bucket protects the file from being publicly accessed.
 * @returns The URL for the file. It's important to use this as it is URL escaped and is exactly what the system stored.
 */
export async function postFile(
  file: Blob,
  name: string,
  preserveFileName = false,
  usePrivateBucket = false,
): StorageResponse<string, null> {
  const data = new FormData();
  data.append("file", file);
  data.append("name", name);

  try {
    const res = await postFormDataWithTimeout(
      `${WEB_API_BASE_PATH}/files?preserveFileName=${preserveFileName}&usePrivateBucket=${usePrivateBucket}`,
      data,
      60 * 1000,
    );

    if (res.ok) {
      return [await res.json(), null];
    } else {
      return [null, await handleFetchError(res)];
    }
  } catch (e) {
    return [null, await handleFetchError(e)];
  }
}

let fileStorageBaseURL: Promise<string | null>;

export async function getFileStorageBaseURL(): Promise<string | null> {
  if (fileStorageBaseURL) {
    return fileStorageBaseURL;
  }
  fileStorageBaseURL = new Promise<string | null>(async (resolve) => {
    try {
      const res = await getWithTimeout(`${WEB_API_BASE_PATH}/files/file-storage-base-url/`);
      if (res.ok) {
        fileStorageBaseURL = await res.json();
        resolve(fileStorageBaseURL);
      }
    } catch (e) {
      console.error(e);
    }
    resolve(null);
  });
  return fileStorageBaseURL;
}

/**
 * Gets the URL of a file that was posted using postFile, with preserveFileName true!
 * @param preservedFileName Same name that was earlier passed to postFile.
 */
export async function getPostedFileURLByName(preservedFileName: string): Promise<string | null> {
  try {
    const baseURL = await getFileStorageBaseURL();
    if (baseURL === null) {
      return null;
    }
    return baseURL + preservedFileName;
  } catch (e) {
    console.error(e);
    return null;
  }
}

export async function listFiles(directoryPath: string): Promise<string[]> {
  const response = await fetchWithTimeout(
    `${WEB_API_BASE_PATH}/files/list-files/?directoryPath=` + encodeURIComponent(directoryPath),
    {
      headers: await getFetchHeaders(true),
    },
  );
  return response.json();
}

setBakedMaterialsFileUrlHelper({
  getPostedFileURLByName,
});
